// Flusso di stato dei PREVENTIVI (cliente e fornitore): specchio di backend/quote_status_flow.py.
// Tabella unica quote_statuses con id FISSI (seed in migrations/align_quotes_to_orders.sql).
// Transizioni MANUALI ("Cambia Stato"): Bozza → Nuovo → Inviato → {Accettato, Rifiutato, Scaduto};
// Cancellato dagli stati aperti; "Evaso" (8) è impostato SOLO dalla conversione in ordine;
// Rifiutato / Cancellato / Scaduto / Evaso sono definitivi. Il backend resta autoritativo.
import { QuoteStatus } from '../types.ts';

export const QUOTE_STATUS = {
  DRAFT: 1,
  NEW: 2,
  SENT: 3,
  ACCEPTED: 4,
  REJECTED: 5,
  CANCELLED: 6,
  EXPIRED: 7,
  COMPLETED: 8,
} as const;

export const QUOTE_STATE_TRANSITIONS: { [key: number]: number[] } = {
  1: [2, 6],        // BOZZA → NUOVO, CANCELLATO
  2: [3, 6],        // NUOVO → INVIATO, CANCELLATO
  3: [4, 5, 7, 6],  // INVIATO → ACCETTATO, RIFIUTATO, SCADUTO, CANCELLATO
  4: [6],           // ACCETTATO → CANCELLATO (Evaso solo dalla conversione in ordine)
  5: [],            // RIFIUTATO → definitivo
  6: [],            // CANCELLATO → definitivo
  7: [],            // SCADUTO → definitivo
  8: [],            // EVASO → definitivo
};

// In creazione un preventivo può nascere solo in Bozza / Nuovo / Inviato (default Nuovo)
export const QUOTE_CREATABLE_STATUS_IDS: number[] = [1, 2, 3];
export const QUOTE_DEFAULT_CREATE_STATUS_ID = QUOTE_STATUS.NEW;
// Righe prodotto modificabili solo prima dell'accettazione (poi solo testata)
export const QUOTE_ROWS_EDITABLE_STATUS_IDS: number[] = [1, 2, 3];
export const QUOTE_TERMINAL_STATUS_IDS: number[] = [5, 6, 7, 8];

export const isQuoteTerminal = (statusId?: number | null): boolean =>
  statusId != null && QUOTE_TERMINAL_STATUS_IDS.includes(statusId);

// Testata modificabile negli stati aperti (Bozza/Nuovo/Inviato/Accettato)
export const isQuoteHeaderEditable = (statusId?: number | null): boolean =>
  statusId != null && !isQuoteTerminal(statusId);

export const areQuoteRowsLocked = (statusId?: number | null): boolean =>
  statusId != null && !QUOTE_ROWS_EDITABLE_STATUS_IDS.includes(statusId);

// Stati selezionabili in un dropdown di stato preventivo:
// - creazione (currentStatusId assente): stati creabili;
// - cambio stato: lo stato attuale (per mostrarlo) + le transizioni autorizzate.
export const getSelectableQuoteStatusIds = (currentStatusId?: number | null): number[] => {
  if (currentStatusId == null) {
    return [...QUOTE_CREATABLE_STATUS_IDS];
  }
  const transitions = QUOTE_STATE_TRANSITIONS[currentStatusId] || [];
  return [currentStatusId, ...transitions];
};

export const QUOTE_STATUS_DESCRIPTIONS: { [key: number]: string } = {
  1: 'Il preventivo è in preparazione: righe e testata sono liberamente modificabili.',
  2: 'Il preventivo è pronto e non è ancora stato inviato.',
  3: 'Il preventivo è stato inviato al cliente/fornitore ed è in attesa di risposta. Da qui può essere Accettato, Rifiutato o dichiarato Scaduto.',
  4: 'Il preventivo è stato accettato: le righe prodotto non sono più modificabili (solo la testata). Da questo stato si può convertire in ordine oppure cancellarlo.',
  5: 'Il preventivo è stato rifiutato (stato definitivo).',
  6: 'Il preventivo è stato cancellato (stato definitivo).',
  7: 'Il preventivo è scaduto senza risposta (stato definitivo).',
  8: 'Il preventivo è stato convertito in ordine (impostato automaticamente dalla conversione).',
};

export const getQuoteStatusColor = (status?: { color?: string | null } | null): string =>
  status?.color || 'default';
